import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash, PaperPlaneRight, Image as ImageIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permanent action stickers (Task 4)
const ACTION_STICKERS = [];  // Removed per user request — Coin Cut/Withdraw Done/Deposit Done moved to chat header

const SavedPhotosPanel = ({ onPhotoSelect, onClose, userId, onActionSticker }) => {
  const [savedPhotos, setSavedPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [amountModal, setAmountModal] = useState(null); // { type, label, color, emoji }
  const [amountValue, setAmountValue] = useState('');

  useEffect(() => { loadSavedPhotos(); }, [userId]);

  const loadSavedPhotos = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/saved-photos?user_id=${userId}`);
      setSavedPhotos(res.data.saved_photos || []);
    } catch (e) {
      console.error('Failed to load saved photos:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only images allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', 'image');
      const uploadRes = await axios.post(`${API}/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

      const saveRes = await axios.post(`${API}/saved-photos`, {
        user_id: userId,
        url: uploadRes.data.url,
        title: file.name
      });

      if (saveRes.data.success) {
        setSavedPhotos([saveRes.data.saved_photo, ...savedPhotos]);
        toast.success('Photo saved!');
      }
    } catch (e) {
      toast.error('Failed to save photo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await axios.delete(`${API}/saved-photos/${photoId}?user_id=${userId}`);
      setSavedPhotos(savedPhotos.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleSend = (photo) => {
    onPhotoSelect({ type: 'image', url: photo.url });
  };

  const openAmount = (sticker) => {
    setAmountModal(sticker);
    setAmountValue('');
  };

  const confirmAmount = () => {
    const amt = String(amountValue || '').trim();
    if (!amt || isNaN(Number(amt.replace(/,/g, ''))) || Number(amt.replace(/,/g, '')) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (typeof onActionSticker === 'function') {
      onActionSticker({ type: amountModal.type, amount: amt });
    }
    setAmountModal(null);
    setAmountValue('');
    onClose && onClose();
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API}${url}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="saved-photos-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <ImageIcon size={24} className="text-orange-500" />
            Saved Photos
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 disabled:opacity-50"
              data-testid="add-saved-photo-btn"
            >
              {isUploading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Plus size={20} />}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={24} />
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden" />

        {/* Action Stickers row removed per user request — Coin Cut/Deposit moved to chat header */}
        {false && (
        <div className="px-3 py-2 border-b bg-gradient-to-b from-gray-50 to-white" data-testid="action-stickers-row">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">Action Stickers</div>
          <div className="grid grid-cols-3 gap-2">
            {ACTION_STICKERS.map(s => (
              <button
                key={s.type}
                onClick={() => openAmount(s)}
                className="rounded-lg py-2 px-2 text-white text-[11px] font-bold flex flex-col items-center justify-center shadow-sm hover:opacity-90 active:scale-95 transition"
                style={{ background: s.color, borderRadius: '999px 999px 12px 12px' }}
                data-testid={`action-sticker-btn-${s.type}`}
              >
                <span className="text-base leading-none">{s.emoji}</span>
                <span className="mt-0.5 uppercase text-[10px] tracking-wide">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
        )}
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : savedPhotos.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No saved photos</p>
              <p className="text-sm text-gray-400 mt-1">Click + to add photos</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {savedPhotos.map(photo => (
                <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={getImageUrl(photo.url)}
                    alt={photo.title || 'Saved'}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Delete button - small, top-right corner */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                    className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                    title="Delete"
                  >
                    <Trash size={12} />
                  </button>
                  {/* Send button - center, large */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleSend(photo)}
                      className="p-3 bg-green-500 text-white rounded-full hover:bg-green-600 shadow-lg"
                      title="Send"
                    >
                      <PaperPlaneRight size={22} weight="fill" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Amount Input Modal for Action Sticker */}
        {amountModal && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setAmountModal(null); }}>
            <div className="bg-white rounded-xl w-full max-w-xs shadow-2xl overflow-hidden" data-testid="action-sticker-amount-modal">
              <div className="px-4 py-3 flex items-center gap-2 text-white" style={{ background: amountModal.color }}>
                <span className="text-2xl">{amountModal.emoji}</span>
                <span className="font-bold text-sm uppercase tracking-wide">{amountModal.label}</span>
              </div>
              <div className="p-4">
                <label className="text-xs text-gray-600 mb-1 block">Amount (₹)</label>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  value={amountValue}
                  onChange={(e) => setAmountValue(e.target.value.replace(/[^0-9,]/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmAmount(); }}
                  placeholder="e.g. 2000"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base font-semibold focus:ring-2 focus:ring-blue-500 outline-none"
                  data-testid="action-sticker-amount-input"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setAmountModal(null)}
                    className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAmount}
                    className="flex-1 py-2 rounded-lg text-white text-sm font-bold shadow"
                    style={{ background: amountModal.color }}
                    data-testid="action-sticker-send-btn"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPhotosPanel;
